import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { 
  Shield, 
  Users, 
  Plus, 
  Search, 
  ChevronRight, 
  ChevronLeft, 
  FolderLock, 
  Check, 
  Activity 
} from "lucide-react";
import api from "../../../connector/api";

const SecurityTab = () => {
    // Master API Pools
    const [groups, setGroups] = useState([]);
    const [allPermissions, setAllPermissions] = useState([]);
    const [allUsers, setAllUsers] = useState([]);

    // Selections State
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [newGroupName, setNewGroupName] = useState("");
    
    // Matrix Tracking states
    const [groupPermissions, setGroupPermissions] = useState([]);
    const [groupUsers, setGroupUsers] = useState([]);
    
    // Temporary Click Buffers
    const [tempAvailablePerms, setTempAvailablePerms] = useState([]);
    const [tempSelectedPerms, setTempSelectedPerms] = useState([]);
    const [tempAvailableUsers, setTempAvailableUsers] = useState([]);
    const [tempSelectedUsers, setTempSelectedUsers] = useState([]);

    // Search filter query bars
    const [permSearch, setPermSearch] = useState("");
    const [userSearch, setUserSearch] = useState("");

    // --- API INITIALIZATION EFFECT BOARDS ---
    useEffect(() => {
        fetchBaseGroups();
        fetchBasePermissions();
        fetchBaseUsers();
    }, []);

    const fetchBaseGroups = async () => {
        try {
        const res = await api.get("groups/");
        setGroups(Array.isArray(res.data) ? res.data : res.data.results || []);
        } catch (err) {
        console.error("Failed to load backend groups table:", err);
        }
    };

    const fetchBasePermissions = async () => {
        try {
        const res = await api.get("custom-permissions/");
        setAllPermissions(Array.isArray(res.data) ? res.data : res.data.results || []);
        } catch (err) {
        console.error("Failed to load permission maps:", err);
        }
    };

    const fetchBaseUsers = async () => {
        try {
            // Hits the unpaginated 'all_users' action to gather the complete dataset for selection transfers
            const res = await api.get("/users/all/");
            setAllUsers(Array.isArray(res.data) ? res.data : []);
            console.log("Normalized Frontend State Array:", res.data);
        } catch (err) {
            console.error("Failed to fetch global users ledger:", err);
        }
        };

    // --- GROUP FOCUS PROFILE SWITCHER ---
    const handleSelectGroup = async (customGroup) => {
        setSelectedGroup(customGroup);
        setTempAvailablePerms([]);
        setTempSelectedPerms([]);
        setTempAvailableUsers([]);
        setTempSelectedUsers([]);
        
        try {
        const res = await api.get(`groups/${customGroup.id}/`);
        setGroupPermissions(res.data.permissions || []);
        setGroupUsers(res.data.users || []); 
        } catch (err) {
        console.error("Failed fetching focus group configurations:", err);
        }
    };

    // --- ACTIONS: CREATE NEW GROUP ---
    const handleCreateGroup = async (e) => {
        e.preventDefault();
        if (!newGroupName.trim()) return toast.error("Group designation cannot remain empty.");

        try {
        const res = await api.post("groups/", { name: newGroupName });
        toast.success(`Group "${newGroupName}" established successfully!`);
        setNewGroupName("");
        fetchBaseGroups();
        handleSelectGroup(res.data);
        } catch (err) {
        console.error(err);
        toast.error("Failed creating group asset registry.");
        }
    };

    // --- ACTIONS: SAVE MATRIX COMPOSITIONS ---
    const handleSaveChanges = async () => {
        if (!selectedGroup) return;
        try {
        // Maps selection matrices into primary key arrays for Django updates
        await api.put(`groups/${selectedGroup.id}/`, {
            permission_ids: groupPermissions.map(p => p.id),
            user_ids: groupUsers.map(u => u.id)
        });
        toast.success("Security profile configurations saved!");
        fetchBaseGroups();
        } catch (err) {
        console.error(err);
        toast.error("Failed persisting authorization schema updates.");
        }
    };

    // --- ARRAYS FILTER CALCULATORS ---
    const unassignedPerms = allPermissions.filter(
        (ap) => !groupPermissions.some((gp) => String(gp.id) === String(ap.id))
    );
    const filteredAvailablePerms = unassignedPerms.filter(p =>
        p?.name?.toLowerCase().includes(permSearch.toLowerCase()) || p?.codename?.toLowerCase().includes(permSearch.toLowerCase())
    );
    const filteredSelectedPerms = groupPermissions.filter(p =>
        p?.name?.toLowerCase().includes(permSearch.toLowerCase()) || p?.codename?.toLowerCase().includes(permSearch.toLowerCase())
    );

    // --- ARRAYS FILTER CALCULATORS ---
    // Optimized lookup using a Set for clean, fast numerical matching
    const assignedUserIds = new Set(groupUsers.map(u => Number(u.id)));

    const unassignedUsers = allUsers.filter(
    (au) => !assignedUserIds.has(Number(au.id))
    );

        // Change the source from 'unassignedUsers' to 'allUsers'
        const filteredAvailableUsers = allUsers.filter(u =>
        u?.username?.toLowerCase().includes(userSearch.toLowerCase()) || 
        `${u?.first_name || ''} ${u?.last_name || ''}`.toLowerCase().includes(userSearch.toLowerCase())
        );

    const filteredSelectedUsers = groupUsers.filter(u => {
    const search = userSearch.toLowerCase();
    const username = (u?.username || "").toLowerCase();
    const fullName = `${u?.first_name || ""} ${u?.last_name || ""}`.toLowerCase();
    return username.includes(search) || fullName.includes(search);
    });

    // --- TRANSFER OPERATORS FOR SELECTION HOOKS ---
    const movePerms = (toSelected) => {
        if (toSelected) {
        const moving = unassignedPerms.filter(p => tempAvailablePerms.includes(p.id));
        setGroupPermissions([...groupPermissions, ...moving]);
        setTempAvailablePerms([]);
        } else {
        setGroupPermissions(groupPermissions.filter(p => !tempSelectedPerms.includes(p.id)));
        setTempSelectedPerms([]);
        }
    };

    const moveUsers = (toSelected) => {
    if (toSelected) {
        // Filter out users who are already present in groupUsers to prevent duplicates
        const moving = allUsers.filter(
        u => tempAvailableUsers.includes(u.id) && !groupUsers.some(gu => String(gu.id) === String(u.id))
        );
        setGroupUsers([...groupUsers, ...moving]);
        setTempAvailableUsers([]);
    } else {
        setGroupUsers(groupUsers.filter(u => !tempSelectedUsers.includes(u.id)));
        setTempSelectedUsers([]);
    }
    };

    return (
        <div className="flex w-full font-dynamic-title gap-6 min-h-[650px] items-start text-zinc-900 selection:bg-zinc-200">
        
        {/* SIDEBAR DIRECTORY PANEL */}
        <div className="w-80 flex flex-col bg-white border border-zinc-200/80 rounded-xl overflow-hidden shadow-[0_2px_8px_-3px_rgba(0,0,0,0.05)] shrink-0 transition-all duration-300">
            <div className="p-4 border-b border-zinc-100 bg-zinc-50/40">
            <div className="flex items-center gap-2 mb-3">
                <Activity className="w-3.5 h-3.5 text-zinc-400" />
                <h3 className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Security Groups</h3>
            </div>
            <form onSubmit={handleCreateGroup} className="flex gap-1.5">
                <input
                type="text"
                placeholder="Create security group..."
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                className="flex-1 bg-zinc-50/60 border border-zinc-200 rounded-lg py-1.5 px-3 text-xs text-zinc-800 placeholder-zinc-400 transition-all focus:bg-white focus:outline-none focus:ring-1 focus:ring-zinc-950 focus:border-zinc-950"
                />
                <button type="submit" className="p-2 bg-[#002855] bg-opacity-80 text-white rounded-lg hover:bg-opacity-95 active:scale-[0.97] transition-all flex items-center justify-center">
                <Plus className="w-3.5 h-3.5" />
                </button>
            </form>
            </div>

            <div className="flex-1 overflow-y-auto p-2 space-y-1 max-h-[580px] custom-scrollbar">
            {groups.length > 0 ? groups.map((g) => {
                const isSelected = selectedGroup?.id === g.id;
                return (
                <div
                    key={g.id}
                    onClick={() => handleSelectGroup(g)}
                    className={`w-full text-left px-4 py-3.5 rounded-xl text-xs font-medium cursor-pointer transition-all duration-200 border relative group ${
                    isSelected
                        ? "bg-[#002855] bg-opacity-80 text-white border-[#002855] border-opacity-80 shadow-[0_4px_12px_rgba(0,0,0,0.1)]"
                        : "bg-white border-transparent text-zinc-600 hover:bg-zinc-50 hover:border-zinc-200/60"
                    }`}
                >
                    <div className="font-semibold text-[13px] tracking-tight flex items-center justify-between">
                    {g.name || "Unnamed Group"}
                    {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />}
                    </div>
                    <div className={`text-[10px] mt-1.5 flex gap-2.5 ${isSelected ? "text-zinc-400" : "text-zinc-400 group-hover:text-zinc-500"}`}>
                    <span className="flex items-center gap-1">🔒 {g.permissions_count || 0} Permissions</span>
                    <span>•</span>
                    <span className="flex items-center gap-1">👥 {g.total_users || 0} Members</span>
                    </div>
                </div>
                );
            }) : (
                <div className="text-[11px] text-zinc-400 text-center py-12 font-medium tracking-wide">No active directories defined</div>
            )}
            </div>
        </div>

        {/* MATRIX CONFIGURATION WORKSPACE AREA */}
        <div className="flex-1 flex flex-col space-y-6">
            {selectedGroup ? (
            <>
                {/* ACTION CAPTIONS PROFILE HUD */}
                <div className="bg-white border border-zinc-200/80 rounded-xl p-5 shadow-[0_2px_8px_-3px_rgba(0,0,0,0.05)] flex justify-between items-center transition-all">
                <div>
                    <h1 className="text-[15px] font-bold tracking-tight text-zinc-900 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-zinc-800 stroke-[1.75]" />
                    {selectedGroup.name}
                    </h1>
                    <p className="text-[10px] font-mono text-zinc-400 mt-1 uppercase tracking-wider">Access Scope Identity Registry // CustomGroup-ID: {selectedGroup.id}</p>
                </div>
                <button
                    onClick={handleSaveChanges}
                    className="px-4 py-2 bg-[#002855] bg-opacity-80 hover:bg-opacity-95 active:scale-[0.98] text-xs font-semibold text-white rounded-lg transition-all shadow-xs"
                >
                    Commit Context Changes
                </button>
                </div>

                {/* PERMISSIONS TRANSFER DECK */}
                <div className="bg-white border border-zinc-200/80 rounded-xl shadow-[0_2px_8px_-3px_rgba(0,0,0,0.05)] overflow-hidden">
                <div className="border-b border-zinc-100 bg-zinc-50/40 px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                    <FolderLock className="w-4 h-4 text-zinc-500 stroke-[1.5]" />
                    <h3 className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Custom Permission Bindings</h3>
                    </div>
                    <div className="relative w-48">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
                    <input
                        type="text"
                        placeholder="Filter rules..."
                        value={permSearch}
                        onChange={(e) => setPermSearch(e.target.value)}
                        className="w-full bg-zinc-50/60 border border-zinc-200 rounded-lg py-1 pl-8 pr-2.5 text-[11px] placeholder-zinc-400 transition-all focus:bg-white focus:outline-none focus:ring-1 focus:ring-zinc-950 focus:border-zinc-950"
                    />
                    </div>
                </div>

                <div className="p-5 grid grid-cols-1 md:grid-cols-[1fr_48px_1fr] gap-4 items-center">
                    <div className="border border-zinc-200/60 rounded-xl overflow-hidden flex flex-col bg-zinc-50/20">
                    <div className="bg-zinc-50/80 border-b border-zinc-100 p-2.5 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Unlinked Custom Perms</div>
                    <div className="p-1.5 h-44 overflow-y-auto space-y-1 custom-scrollbar">
                        {filteredAvailablePerms.map(p => {
                        const active = tempAvailablePerms.includes(p.id);
                        return (
                            <div
                            key={p.id}
                            onClick={() => setTempAvailablePerms(prev => active ? prev.filter(i => i !== p.id) : [...prev, p.id])}
                            className={`p-2 rounded-lg border text-xs cursor-pointer transition-all duration-150 flex items-center justify-between ${
                                active ? "bg-[#002855] bg-opacity-80 text-white border-[#002855] border-opacity-80" : "bg-white text-zinc-600 border-zinc-100 hover:bg-zinc-50 hover:border-zinc-200"
                            }`}
                            >
                            <div>
                                <div className="font-semibold tracking-tight text-[11px]">{p.name}</div>
                                <div className="text-[9px] font-mono text-zinc-400 mt-0.5">{p.codename}</div>
                            </div>
                            {active && <Check className="w-3 h-3 text-white" />}
                            </div>
                        );
                        })}
                    </div>
                    </div>

                    <div className="flex md:flex-col items-center justify-center gap-1.5">
                    <button onClick={() => movePerms(true)} disabled={tempAvailablePerms.length === 0} className="p-2 bg-[#002855] bg-opacity-80 text-white border-[#002855] border-opacity-80 rounded-lg disabled:opacity-20 active:scale-95 transition-all shadow-2xs"><ChevronRight className="w-3.5 h-3.5" /></button>
                    <button onClick={() => movePerms(false)} disabled={tempSelectedPerms.length === 0} className="p-2 bg-[#002855] bg-opacity-80 text-white border-[#002855] border-opacity-80 rounded-lg disabled:opacity-20 active:scale-95 transition-all shadow-2xs"><ChevronLeft className="w-3.5 h-3.5" /></button>
                    </div>

                    <div className="border border-zinc-200/80 rounded-xl overflow-hidden flex flex-col bg-zinc-50/20">
                    <div className="bg-zinc-50/80 border-b border-zinc-100 p-2.5 text-[10px] font-bold text-zinc-800 uppercase tracking-wider">Assigned Permissions</div>
                    <div className="p-1.5 h-44 overflow-y-auto space-y-1 custom-scrollbar">
                        {filteredSelectedPerms.map(p => {
                        const active = tempSelectedPerms.includes(p.id);
                        return (
                            <div
                            key={p.id}
                            onClick={() => setTempSelectedPerms(prev => active ? prev.filter(i => i !== p.id) : [...prev, p.id])}
                            className={`p-2 rounded-lg border text-xs cursor-pointer transition-all duration-150 flex items-center justify-between ${
                                active ? "bg-zinc-100 border-zinc-300 text-zinc-900" : "bg-white text-zinc-600 border-zinc-100 hover:bg-zinc-50 hover:border-zinc-200"
                            }`}
                            >
                            <div>
                                <div className="font-semibold tracking-tight text-[11px]">{p.name}</div>
                                <div className="text-[9px] font-mono text-zinc-400 mt-0.5">{p.codename}</div>
                            </div>
                            {active && <span className="w-1.5 h-1.5 rounded-full bg-zinc-900" />}
                            </div>
                        );
                        })}
                    </div>
                    </div>
                </div>
                </div>

                {/* USERS TRANSFER DECK */}
                <div className="bg-white border border-zinc-200/80 rounded-xl shadow-[0_2px_8px_-3px_rgba(0,0,0,0.05)] overflow-hidden">
                <div className="border-b border-zinc-100 bg-zinc-50/40 px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-zinc-500 stroke-[1.5]" />
                    <h3 className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">User Set Membership</h3>
                    </div>
                    <div className="relative w-48">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
                    <input
                        type="text"
                        placeholder="Filter members..."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        className="w-full bg-zinc-50/60 border border-zinc-200 rounded-lg py-1 pl-8 pr-2.5 text-[11px] placeholder-zinc-400 transition-all focus:bg-white focus:outline-none focus:ring-1 focus:ring-zinc-950 focus:border-zinc-950"
                    />
                    </div>
                </div>

                <div className="p-5 grid grid-cols-1 md:grid-cols-[1fr_48px_1fr] gap-4 items-center">
                    <div className="border border-zinc-200/60 rounded-xl overflow-hidden flex flex-col bg-zinc-50/20">
                    <div className="bg-zinc-50/80 border-b border-zinc-100 p-2.5 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Available Users</div>
                    <div className="p-1.5 h-44 overflow-y-auto space-y-1 custom-scrollbar">
                        {filteredAvailableUsers.map(u => {
                        const active = tempAvailableUsers.includes(u.id);
                        return (
                            <div
                            key={u.id}
                            onClick={() => setTempAvailableUsers(prev => active ? prev.filter(i => i !== u.id) : [...prev, u.id])}
                            className={`p-2 rounded-lg border text-xs cursor-pointer transition-all duration-150 flex items-center justify-between ${
                                active ? "bg-[#002855] bg-opacity-80 text-white border-[#002855] border-opacity-80" : "bg-white text-zinc-600 border-zinc-100 hover:bg-zinc-50 hover:border-zinc-200"
                            }`}
                            >
                            <div>
                                <div className="font-semibold tracking-tight text-[11px]">{u.username}</div>
                                <div className="text-[9px] text-zinc-400 mt-0.5">{u.first_name} {u.last_name}</div>
                            </div>
                            {active && <Check className="w-3 h-3 text-white" />}
                            </div>
                        );
                        })}
                    </div>
                    </div>

                    <div className="flex md:flex-col items-center justify-center gap-1.5">
                    <button onClick={() => moveUsers(true)} disabled={tempAvailableUsers.length === 0} className="p-2 bg-[#002855] bg-opacity-80 text-white border-[#002855] border-opacity-80 rounded-lg disabled:opacity-20 active:scale-95 transition-all shadow-2xs"><ChevronRight className="w-3.5 h-3.5" /></button>
                    <button onClick={() => moveUsers(false)} disabled={tempSelectedUsers.length === 0} className="p-2 bg-[#002855] bg-opacity-80 text-white border-[#002855] border-opacity-80 rounded-lg disabled:opacity-20 active:scale-95 transition-all shadow-2xs"><ChevronLeft className="w-3.5 h-3.5" /></button>
                    </div>

                    <div className="border border-zinc-200/80 rounded-xl overflow-hidden flex flex-col bg-zinc-50/20">
                    <div className="bg-zinc-50/80 border-b border-zinc-100 p-2.5 text-[10px] font-bold text-zinc-800 uppercase tracking-wider">Enrolled Members</div>
                    <div className="p-1.5 h-44 overflow-y-auto space-y-1 custom-scrollbar">
                        {filteredSelectedUsers.map(u => {
                        const active = tempSelectedUsers.includes(u.id);
                        return (
                            <div
                            key={u.id}
                            onClick={() => setTempSelectedUsers(prev => active ? prev.filter(i => i !== u.id) : [...prev, u.id])}
                            className={`p-2 rounded-lg border text-xs cursor-pointer transition-all duration-150 flex items-center justify-between ${
                                active ? "bg-zinc-100 border-zinc-300 text-zinc-900" : "bg-white text-zinc-600 border-zinc-100 hover:bg-slate-50 hover:border-zinc-200"
                            }`}
                            >
                            <div>
                                <div className="font-semibold tracking-tight text-[11px]">{u.username}</div>
                                <div className="text-[9px] text-zinc-400 mt-0.5">{u.first_name} {u.last_name}</div>
                            </div>
                            {active && <span className="w-1.5 h-1.5 rounded-full bg-zinc-900" />}
                            </div>
                        );
                        })}
                    </div>
                    </div>
                </div>
                </div>
            </>
            ) : (
            <div className="flex-1 flex flex-col justify-center items-center bg-white border border-zinc-200 border-dashed rounded-xl p-16 text-center text-zinc-400 min-h-[400px]">
                <div className="w-10 h-10 rounded-full bg-zinc-50 flex items-center justify-center border border-zinc-100 mb-3 shadow-2xs">
                <Shield className="w-5 h-5 text-zinc-400 stroke-[1.25]" />
                </div>
                <p className="text-xs font-semibold text-zinc-800">No Profile Active</p>
                <p className="text-[11px] text-zinc-400 mt-1 max-w-[280px]">Select an access scope profile context from the directory engine list sidebar to configure security schemas.</p>
            </div>
            )}
        </div>

        </div>
    );
};

export default SecurityTab;